import { Info, Lightbulb, PlayCircle } from "lucide-react";

export const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <section id={id} className="scroll-mt-20 border-b border-zinc-200/70 pb-9 last:border-0">
      <h2 className="font-display mb-5 text-3xl tracking-tight text-zinc-900">{title}</h2>
      {children}
    </section>
  );
};

export const P = ({ children }: { children: React.ReactNode }) => {
  return <p className="mb-4 text-base leading-7 text-zinc-600">{children}</p>;
};

export const InlineCode = ({ children }: { children: React.ReactNode }) => {
  return (
    <code className="bg-zinc-100 px-1.5 py-0.5 font-mono text-sm text-zinc-800">
      {children}
    </code>
  );
};

export const Callout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="border-l-2 border-zinc-300 pl-5 py-1 mb-5 text-base text-zinc-500 italic leading-relaxed">
      {children}
    </div>
  );
};

export const Tip = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="my-5 flex gap-3 border-l border-zinc-300 py-1 pl-4">
      <Lightbulb className="mt-1 h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <div className="text-sm leading-6 text-zinc-600">{children}</div>
    </div>
  );
};

export const Note = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="my-5 flex gap-3 border-l border-zinc-300 py-1 pl-4">
      <Info className="mt-1 h-3.5 w-3.5 shrink-0 text-zinc-400" />
      <div className="text-sm leading-6 text-zinc-600">{children}</div>
    </div>
  );
};

export const Step = ({ n, children }: { n: number; children: React.ReactNode }) => {
  return (
    <div className="mb-5 flex gap-4">
      <div className="mt-0.5 shrink-0 text-base italic text-zinc-400">{n}.</div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
};

export const docTextLinkClass =
  "text-zinc-800 underline decoration-zinc-300 underline-offset-4 hover:text-zinc-950 hover:decoration-zinc-600";

export const VideoPlaceholder = ({ title }: { title: string }) => {
  return (
    <div className="my-5 flex flex-col items-center justify-center gap-1.5 border border-zinc-200 py-10 text-center">
      <PlayCircle className="w-8 h-8 text-zinc-300" />
      <div className="text-sm font-medium text-zinc-500">{title}</div>
      <div className="text-xs text-zinc-400">Video walkthrough coming soon</div>
    </div>
  );
};

export const MethodBadge = ({ kind }: { kind: "both" | "mcp" }) => {
  return kind === "both" ? (
    <span className="mb-4 inline-flex items-center gap-2 text-2xs uppercase tracking-[0.1em] text-zinc-400">
      <span>Local install</span>
      <span aria-hidden>/</span>
      <span>MCP server</span>
    </span>
  ) : (
    <span className="mb-4 inline-flex text-2xs uppercase tracking-[0.1em] text-zinc-400">
      <span>MCP server only</span>
    </span>
  );
};

export const MCP_URL = "https://paperclip.gxl.ai/mcp";
